import { useState, useEffect } from 'react';
// @mui
import {
  Card,
  Button,
  Container,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton
} from '@mui/material';
// routes
import axios, { getErrorMessage } from 'utils/axios';
import useIsMountedRef from 'hooks/useIsMountedRef';
import { fDate } from 'utils/formatTime';
import { PATH_DASHBOARD } from '../../routes/paths';
// hooks
import useSettings from '../../hooks/useSettings';
import { useSnackbar } from 'notistack';
// components
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
// sections
import { DataGrid } from '@mui/x-data-grid';
import { fCurrency, removeTrailingZeros } from 'utils/formatNumber';
import { LoadingButton } from '@mui/lab';
import CustomGridToolbar from 'components/CustomGridToolbar';
import SearchQueryDailog from 'sections/@dashboard/SearchQueryDailog';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ProfileDialog from 'sections/@dashboard/user/ProfileDialog';
import BlockchainTransactionDialog from 'sections/@dashboard/BlockchainTransactionDialog';


export default function InstantSwapLog() {
  const { themeStretch } = useSettings();
  const isMountedRef = useIsMountedRef();
  const [sort, setSort] = useState({field:'id', order:'desc',page_size:100,page:0});
  const [data, setData] = useState({data:[],current_page:1,total_pages:null,total_size:0,page_size:0});
  const [state,setState] = useState({loading:false,loadComplete:false,error:null});
  const [dailog,setDailog] = useState({open:false,query:"",});
  const [profileDialog,setProfileDialog] = useState({open:false,user:"",});
  const [search,setSearch] = useState({open:false,query:"", list:[]});
  const [trans,setTrans] = useState({open:false,txid:''});
  const { enqueueSnackbar } = useSnackbar();


  useEffect(() => {
    getInitData();
    // This page still uses the existing imperative loader pattern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page title="Instant Swaps">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Instant Swap"
          links={[
            { name: 'Transactions', href: PATH_DASHBOARD.transactions.root },
            { name: 'Instant Swap' },
          ]}
          action={
            <>
            <Button
              variant="contained"
              component={Button}
              onClick={()=>{
                setDailog({open:true, isEdit: false});
              }}
              startIcon={<Iconify icon={'cil:search'} />}
            >
             Search
            </Button>
            </>
          }
        />

        {!state.loading && state.error !=null && (
          <Card>
            <Box padding={10} display={"flex"} justifyContent={"center"}>
            <p>Error {state.error} </p>
            </Box>
          </Card>
        )}

        <Card>
          {search.open && 
          <Box sx={{display:"flex", alignContent:"right", justifyContent:"space-between", width:"100%", padding:2}}>
            <p>Showing results for: {search.query}</p>
            <LoadingButton sx={{mr:2}}
              startIcon={<Iconify icon={'carbon:close-filled'} />}
              loading={state.loading} 
              onClick={()=> setSearch({...search, open:false})}
               variant='outlined' color="error">
                Close search
            </LoadingButton>
          </Box>}
          {!search.open && 
            <Box sx={{display:"flex", alignContent:"right ", justifyContent:"end", width:"100%", padding:2}}>
            <FormControl sx={{ml:2,width:180}}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker 
                  value={sort.start_date}
                  label="From date"
                  onChange={(newValue) => setSort({...sort,start_date:newValue})}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </LocalizationProvider>
            </FormControl>
            <FormControl sx={{ml:2,width:180}}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker 
                  value={sort.end_date}
                  label="To date"
                  onChange={(newValue) => setSort({...sort,end_date:newValue})}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </LocalizationProvider>
            </FormControl>
            <FormControl sx={{ml:2,width:180}}>
              <TextField
                fullWidth
                size="small"
                label="Status"
                value={sort.status || ''}
                onChange={(e)=> setSort({...sort,status:e.target.value})}
              />
            </FormControl>
            <FormControl sx={{ml:2,width:180}}>
                <InputLabel id="demo-simple-select-label2">Sort date</InputLabel>
                <Select
                  labelId="demo-simple-select-label2"
                  label="Sort balance"
                  value={sort.order}
                  onChange={(e)=> setSort({...sort,order:e.target.value})}
                >
                  {[
                    {v:'asc',n:"Ascending"},
                    {v:'desc',n:"Descending"},
                    ].map((opt,i) => (
                  <MenuItem key={i} value={opt.v}>{opt.n}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <LoadingButton loading={state.loading} onClick={()=> getInitData()}
                sx={{ml:2}} variant='outlined' color="secondary">
                  Apply changes
              </LoadingButton> 
              
            </Box>}

          <Box sx={{ height: 650, width: '100%' }}>
            <DataGrid
              rows={search.open ? search.list : data.data}
              columns={[
                {
                  field: 'name',
                  headerName: 'User',
                  width: 220,
                  editable: false,
                  renderCell: (params) => (
                    <>
                      <IconButton color="primary" onClick={()=> setProfileDialog({open:true,user:{user_id:params.row.user_id}}) }>
                        <Iconify icon={'ph:user-thin'} size="small" sx={{width:25,height:25}} />
                      </IconButton>
                      <Typography variant="subtitle2">
                        {params.row.first_name || ''} {params.row.last_name || ''}
                      </Typography>
                    </>
                  ),
                  valueGetter: (params) => `${params.row.first_name} ${params.row.last_name}`
                },
                {
                  field: 'from_amount',
                  headerName: 'From Amount',
                  width: 200,
                  editable: false,
                  valueGetter: (params) => `${params.row.from_currency}${removeTrailingZeros(params.value)}`,
                  renderCell: (params) => (
                    <p>
                      <IconButton color="primary" onClick={() =>  setTrans({open:true,txid: params.row.txid})}>
                        <Iconify icon={'eva:expand-outline'} size="small" sx={{ width: 25, height: 25 }} />
                      </IconButton>
                      {params.value}
                    </p>
                  ),
                },
                // {
                //   field: 'from_amount',
                //   headerName: 'From Amount',
                //   width: 180,
                //   editable: false,
                //    valueGetter: (params) => `${params.row.from_currency}${removeTrailingZeros(params.value)}`
                // },
                {
                  field: 'to_amount',
                  headerName: 'To Amount',
                  width: 180,
                  editable: false,
                  valueGetter: (params) => `${params.row.to_currency}${fCurrency(removeTrailingZeros(params.value))}`
                },
                {
                  field: 'status',
                  headerName: 'Status',
                  width: 220,
                  editable: false,
                },
                {
                  field: 'created_at',
                  headerName: 'Created at',
                  width: 180,
                  editable: false,
                  valueGetter: (params) => fDate(params.value),
                },
                {
                  field: 'quote_id',
                  headerName: 'Quote ID',
                  width: 180,
                  editable: false,
                },
              ]}
              
              loading={state.loading}
              checkboxSelection
              disableRowSelectionOnClick
              components={{ Toolbar: CustomGridToolbar }}
              disableSelectionOnClick

              paginationMode="server"
              rowCount={data.total_size}
              page={data.current_page - 1}
              rowsPerPage={data.data.length}
              onPageChange={(newPage)=> {
                setSort({...sort, page:newPage + 1});
                getInitData(newPage + 1);
              }}
              pageSize={data.page_size}
              onPageSizeChange={(newPage) => setSort({...sort, page_size:newPage})}
              rowsPerPageOptions={[]}
              
            />
          </Box>
        </Card>
      </Container>


      {dailog.open &&  <SearchQueryDailog
        open={dailog.open}
        hint='Search database for matching records'
        label="Enter Quote ID"
        handleClose={()=>{
          setDailog({open:false});
        }}
        onSubmit={(val)=>{
          setDailog({open:false});
          searchUser(val);
        }}
        />}


      {profileDialog.open &&  <ProfileDialog
        user={profileDialog.user}
        refresh
        handleClose={()=>{
          setProfileDialog({open:false});
        }}
         />}

      {trans.open &&  <BlockchainTransactionDialog
          txid={trans.txid}
          refresh
          handleClose={()=>{
            setTrans({open:false});
          }}
          />}

    </Page>
  );

  async function getInitData(page = null) {
    
    setState({...state,loading:true,loadComplete:false,error:null});

    const sdate = sort.start_date ?  new Date(sort.start_date).toDateString() : '';
    const edate = sort.end_date ?  new Date(sort.end_date).toDateString() : '';

    try {
      let str = `?sort_order=${sort.order}&page=${page || sort.page}&page_size=${sort.page_size}`;
      str+= `&start_date=${sdate}&end_date=${edate}&status=${sort.status || ''}`;

      const response = await axios.get(`/transactions/instant_swaps${str}`);

      if (isMountedRef.current) {
        setData(response.data);
      }
      setState({...state,loading:false,loadComplete:true,error:null})
    } catch (error) {
      // console.error(error);
      setState({...state,loading:false,loadComplete:false,error: getErrorMessage(error)})
    }
  };

  async function searchUser(query) {
    
    setState({...state,loading:true});
    setSearch({open:true,query,list:[]});

    try {
      const response = await axios.get(`/transactions/instant_swaps/${encodeURIComponent(query)}`);

      if (isMountedRef.current) {
        setSearch({open:true,query, list:Array.isArray(response.data) ? response.data : []});
        setState({...state,loading:false});
      }
    } catch (error) {
      // console.error(error);
      setState({...state,loading:false});
      enqueueSnackbar(getErrorMessage(error),{variant:'warning'});
    }
  };


}
